import moment from 'moment'
import { isoCountries } from '../../utils/countries'

// Pure helpers for the growth bot's pages (spec: 0042_growth_bot, w-admin).

export const KIND_LABELS = {
  car_event: 'Car event',
  sourcing: 'Sourcing fair',
  diaspora: 'Diaspora',
  partner: 'Partner lead'
}

export const STATUS_LABELS = {
  new: 'New',
  sent: 'Sent',
  interested: 'Interested',
  dismissed: 'Not for us',
  snoozed: 'Remind me later'
}

export const STATUS_COLORS = {
  new: 'blue',
  sent: 'default',
  interested: 'green',
  dismissed: 'default',
  snoozed: 'gold'
}

export const REACH_LABELS = {
  reachable: { label: 'In reach', color: 'green' },
  funded: { label: 'Funded', color: 'blue' },
  out: { label: 'Out of reach', color: 'default' }
}

export const JOB_LABELS = {
  opportunities: 'Opportunities',
  review: 'Weekly review',
  reminders: 'Reminders'
}

// Read a value whichever way the API's camelizer left it (nested dicts in lists come back camelCased,
// top-level fields too; a key with no underscore is the same either way).
const pick = (obj, camel, snake) => (obj ? (obj[camel] !== undefined ? obj[camel] : obj[snake]) : undefined)

// "28 Nov – 6 Dec 2026"; one day prints once.
export const dateRange = (start, end) => {
  if (!start) return ''
  const s = moment(start)
  if (!end || end === start) return s.format('D MMM YYYY')
  const e = moment(end)
  if (s.year() !== e.year()) return `${s.format('D MMM YYYY')} – ${e.format('D MMM YYYY')}`
  if (s.month() !== e.month()) return `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`
  return `${s.format('D')}–${e.format('D MMM YYYY')}`
}

// past / soon (within 14 days) / later / none, for the deadline's colour.
export const deadlineState = (deadline, now = moment()) => {
  if (!deadline) return 'none'
  const days = moment(deadline).startOf('day').diff(moment(now).startOf('day'), 'days')
  if (days < 0) return 'past'
  if (days <= 14) return 'soon'
  return 'later'
}

export const buildOpportunityQuery = ({ kind, status, reach, startsWithin, search, page } = {}) => {
  const params = new URLSearchParams()
  if (kind) params.set('kind', kind)
  if (status) params.set('status', status)
  if (reach) params.set('reach', reach)
  if (startsWithin && startsWithin !== 'any') params.set('starts_within', String(startsWithin))
  if (search && search.trim()) params.set('search', search.trim())
  if (page && page > 1) params.set('page', String(page))
  const query = params.toString()
  return query ? `?${query}` : ''
}

const DATE_FIELDS = ['startsOn', 'endsOn', 'deadlineOn']

// Form values to the API: dates as YYYY-MM-DD (an empty date as null), text trimmed, the country code
// upper case. Empty text stays '' because the API's text fields are blank, never null.
export const toOpportunityPayload = values => {
  const out = {}
  Object.entries(values || {}).forEach(([key, value]) => {
    if (DATE_FIELDS.includes(key)) {
      out[key] = value ? moment(value).format('YYYY-MM-DD') : null
    } else if (typeof value === 'string') {
      out[key] = value.trim()
    } else if (value !== undefined) {
      out[key] = value
    }
  })
  if (out.countryCode) out.countryCode = out.countryCode.toUpperCase()
  return out
}

const change = (now, before) => {
  const a = Number(now)
  const b = Number(before)
  if (!b || Number.isNaN(a) || Number.isNaN(b)) return '—'
  const pct = Math.round(((a - b) / b) * 100)
  return `${pct >= 0 ? '+' : ''}${pct}%`
}

// The review's numbers as table rows, each with last week's value and the change.
export const numbersRows = (thisWeek, lastWeek) => {
  const now = thisWeek || {}
  const before = lastWeek || {}
  const rows = [
    { key: 'chats', label: 'Chats', now: now.chats, before: before.chats }
  ]
  const previous = {}
  ;(before.services || []).forEach(s => {
    previous[s.key] = s
  })
  ;(now.services || []).forEach(s => {
    const p = previous[s.key] || {}
    rows.push({ key: `${s.key}-orders`, label: `${s.label}: orders`, now: s.orders, before: p.orders })
    rows.push({ key: `${s.key}-paid`, label: `${s.label}: paid`, now: s.paid, before: p.paid })
  })
  const ads = now.ads || {}
  const adsBefore = before.ads || {}
  rows.push({ key: 'ads', label: 'Ad arrivals: paid %', now: ads.conversion, before: adsBefore.conversion })
  const others = now.others || {}
  const othersBefore = before.others || {}
  rows.push({ key: 'others', label: 'Everyone else: paid %', now: others.conversion, before: othersBefore.conversion })
  rows.push({
    key: 'handed',
    label: 'Handed to our team',
    now: pick(now, 'handedToTeam', 'handed_to_team'),
    before: pick(before, 'handedToTeam', 'handed_to_team')
  })
  const d = now.decisions || {}
  const dBefore = before.decisions || {}
  rows.push({ key: 'lapsed', label: 'Questions that timed out', now: d.lapsed, before: dBefore.lapsed })
  rows.push({ key: 'parked', label: 'Parked work', now: now.parked, before: before.parked })
  return rows
    .filter(r => r.now !== undefined)
    .map(r => ({ ...r, before: r.before === undefined ? '—' : r.before, change: change(r.now, r.before) }))
}

// "Stopped at" pairs from the review's numbers, biggest first.
export const stoppedAt = thisWeek =>
  Object.entries(pick(thisWeek, 'stoppedAt', 'stopped_at') || {}).sort((a, b) => b[1] - a[1])

// The setup checklist, in the order the owner does it.
export const setupChecklist = status => {
  const s = status || {}
  return [
    {
      key: 'token',
      ok: Boolean(s.tokenSet),
      label: 'Bot token',
      todo: 'Create the bot with @BotFather and set GROWTH_BOT_TOKEN and GROWTH_BOT_WEBHOOK_SECRET on the server.'
    },
    {
      key: 'webhook',
      ok: Boolean(s.webhookSet),
      label: 'Webhook',
      todo: s.webhookError
        ? `Telegram says: ${s.webhookError}`
        : 'Run `python manage.py register_growth_bot` on the server.'
    },
    {
      key: 'chat',
      ok: Boolean(s.chatSet),
      label: 'Chat',
      todo: 'Send /start to the bot and paste the chat id it shows below.'
    },
    {
      key: 'enabled',
      ok: Boolean(s.enabled),
      label: 'Switched on',
      todo: 'Turn on the switch below so the schedule runs.'
    }
  ]
}

export const runningRun = runs => (runs || []).some(r => r.running)

// Places split into the two lists, each sorted by country then city.
export const splitPlaces = places => {
  const sorted = [...(places || [])].sort((a, b) => {
    const an = a.countryName || ''
    const bn = b.countryName || ''
    if (an !== bn) return an.localeCompare(bn)
    return (a.city || '').localeCompare(b.city || '')
  })
  return {
    inReach: sorted.filter(p => p.reachable),
    outOfReach: sorted.filter(p => !p.reachable)
  }
}

// ISO code to flag emoji ("DE" → 🇩🇪).
export const flag = code =>
  (code || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .replace(/./g, ch => String.fromCodePoint(127397 + ch.charCodeAt(0)))

export const countryOptions = () =>
  Object.entries(isoCountries)
    .map(([code, name]) => ({ value: code, label: `${flag(code)} ${name}` }))
    .sort((a, b) => a.label.slice(3).localeCompare(b.label.slice(3)))

export const formatUsd = value => `$${Number(value || 0).toFixed(2)}`
